import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  Card, CardContent, CardHeader, CardTitle,
} from '../components/ui/card';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Plus, Trash2, FileText, CheckCircle2, XCircle, ArrowRightCircle, Eye, Download } from 'lucide-react';
import { useIsAdmin } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const STATUS_COLORS = {
  draft: 'bg-slate-100 text-slate-700',
  sent: 'bg-blue-100 text-blue-700',
  approved: 'bg-emerald-100 text-emerald-700',
  rejected: 'bg-rose-100 text-rose-700',
  converted: 'bg-indigo-100 text-indigo-700',
};

const emptyItem = { product_id: '', quantity: '', selling_price: '', gst_percentage: 12, discount_percentage: 0, buyer_item_code: '' };
const emptyForm = () => ({
  customer_id: '',
  quotation_date: new Date().toISOString().split('T')[0],
  valid_until: '',
  customer_reference: '',
  notes: '',
  items: [{ ...emptyItem }],
});

const SalesQuotations = () => {
  const isAdmin = useIsAdmin();
  const [rows, setRows] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(false);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);

  const [viewing, setViewing] = useState(null);
  const [convertOpen, setConvertOpen] = useState(false);
  const [convertBatches, setConvertBatches] = useState([]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [q, c, p, inv] = await Promise.all([
        axios.get(`${API_URL}/sales-quotations`),
        axios.get(`${API_URL}/customers`),
        axios.get(`${API_URL}/products`),
        axios.get(`${API_URL}/inventory`),
      ]);
      setRows(q.data || []);
      setCustomers(c.data || []);
      setProducts(p.data || []);
      setInventory(inv.data || []);
    } catch (_e) { toast.error('Failed to load'); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchAll(); }, []);

  const openCreate = () => { setEditing(null); setForm(emptyForm()); setDialogOpen(true); };
  const openEdit = (q) => {
    setEditing(q);
    setForm({
      customer_id: q.customer_id,
      quotation_date: q.quotation_date,
      valid_until: q.valid_until || '',
      customer_reference: q.customer_reference || '',
      notes: q.notes || '',
      items: q.items.map(it => ({
        product_id: it.product_id,
        quantity: it.quantity,
        selling_price: it.selling_price,
        gst_percentage: it.gst_percentage,
        discount_percentage: it.discount_percentage || 0,
        buyer_item_code: it.buyer_item_code || '',
      })),
    });
    setDialogOpen(true);
  };

  const setItem = (idx, patch) => {
    setForm(prev => {
      const items = [...prev.items];
      items[idx] = { ...items[idx], ...patch };
      return { ...prev, items };
    });
  };
  const onProductChange = (idx, productId) => {
    const p = products.find(x => x.id === productId);
    setItem(idx, {
      product_id: productId,
      selling_price: p?.selling_price || '',
      gst_percentage: p?.gst_percentage || 12,
      buyer_item_code: p?.buyer_item_code || '',
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.customer_id) { toast.error('Select a customer'); return; }
    if (!form.items.length || !form.items[0].product_id) { toast.error('Add at least one item'); return; }
    setSaving(true);
    try {
      const payload = {
        customer_id: form.customer_id,
        quotation_date: form.quotation_date,
        valid_until: form.valid_until || null,
        customer_reference: form.customer_reference?.trim() || null,
        notes: form.notes?.trim() || null,
        items: form.items.filter(i => i.product_id && i.quantity).map(i => ({
          product_id: i.product_id,
          quantity: parseInt(i.quantity),
          selling_price: parseFloat(i.selling_price),
          gst_percentage: parseFloat(i.gst_percentage),
          discount_percentage: parseFloat(i.discount_percentage || 0),
          buyer_item_code: (i.buyer_item_code || '').trim() || null,
        })),
      };
      if (editing) {
        await axios.put(`${API_URL}/sales-quotations/${editing.id}`, payload);
        toast.success('Quotation updated');
      } else {
        await axios.post(`${API_URL}/sales-quotations`, payload);
        toast.success('Quotation created');
      }
      setDialogOpen(false); await fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const setStatus = async (q, status) => {
    try {
      await axios.patch(`${API_URL}/sales-quotations/${q.id}/status`, { status });
      toast.success(`Marked ${status}`);
      await fetchAll();
      if (viewing?.id === q.id) setViewing({ ...viewing, status });
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const openConvert = (q) => {
    setViewing(q);
    setConvertBatches(q.items.map(it => ({ product_id: it.product_id, batch_number: '' })));
    setConvertOpen(true);
  };
  const batchesForProduct = (pid) => inventory.filter(inv => inv.product_id === pid && inv.quantity > 0);
  const runConvert = async () => {
    if (convertBatches.some(b => !b.batch_number)) { toast.error('Select a batch for every line'); return; }
    try {
      const res = await axios.post(`${API_URL}/sales-quotations/${viewing.id}/convert`, {
        invoice_date: new Date().toISOString().split('T')[0],
        batches: convertBatches,
      });
      toast.success(`Converted → ${res.data.sale.invoice_number}`);
      setConvertOpen(false); setViewing(null); await fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Conversion failed'); }
  };

  const del = async (q) => {
    if (!window.confirm(`Delete quotation ${q.quotation_number}?`)) return;
    try { await axios.delete(`${API_URL}/sales-quotations/${q.id}`); toast.success('Deleted'); await fetchAll(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const downloadPdf = async (q) => {
    try {
      const res = await axios.get(`${API_URL}/sales-quotations/${q.id}/download-pdf`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(q.quotation_number || 'quotation').replace(/\//g, '_')}.pdf`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success('Downloaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'PDF download failed');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-['Manrope'] flex items-center gap-2">
            <FileText className="w-6 h-6 text-teal-600" /> Sales Quotations
          </h1>
          <p className="text-slate-500 text-sm mt-1">Customer-facing quotations. Buyer Item Codes flow into the invoice on conversion.</p>
        </div>
        {isAdmin && <Button onClick={openCreate} className="bg-teal-600 hover:bg-teal-700" data-testid="sq-create-btn"><Plus className="w-4 h-4 mr-2" /> New Quotation</Button>}
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>#</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Customer</TableHead>
                <TableHead>Reference</TableHead>
                <TableHead className="text-right">Total</TableHead>
                <TableHead className="w-[120px] text-center">Status</TableHead>
                <TableHead className="w-[220px] text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={7} className="text-center py-8 text-sm text-slate-500">Loading…</TableCell></TableRow>
              ) : rows.length === 0 ? (
                <TableRow><TableCell colSpan={7} className="text-center py-8 text-sm text-slate-500">No quotations yet.</TableCell></TableRow>
              ) : rows.map(q => (
                <TableRow key={q.id} data-testid={`sq-row-${q.id}`}>
                  <TableCell className="tabular-nums font-medium">{q.quotation_number}</TableCell>
                  <TableCell>{q.quotation_date}</TableCell>
                  <TableCell>{q.customer_name}</TableCell>
                  <TableCell className="text-xs text-slate-500">{q.customer_reference || '—'}</TableCell>
                  <TableCell className="text-right tabular-nums">₹{Number(q.grand_total || 0).toFixed(2)}</TableCell>
                  <TableCell className="text-center"><Badge className={STATUS_COLORS[q.status] || 'bg-slate-100 text-slate-700'}>{q.status}</Badge></TableCell>
                  <TableCell className="text-right">
                    <div className="inline-flex gap-1">
                      <Button size="sm" variant="ghost" onClick={() => setViewing(q)} data-testid={`sq-view-${q.id}`}><Eye className="w-4 h-4" /></Button>
                      <Button size="sm" variant="ghost" onClick={() => downloadPdf(q)} title="Download PDF" data-testid={`sq-download-${q.id}`}><Download className="w-4 h-4 text-teal-600" /></Button>
                      {isAdmin && q.status === 'draft' && <Button size="sm" variant="outline" onClick={() => setStatus(q, 'sent')} data-testid={`sq-send-${q.id}`}>Send</Button>}
                      {isAdmin && q.status === 'sent' && (<>
                        <Button size="sm" variant="outline" onClick={() => setStatus(q, 'approved')} data-testid={`sq-approve-${q.id}`}><CheckCircle2 className="w-3.5 h-3.5 mr-1" />Approve</Button>
                        <Button size="sm" variant="ghost" onClick={() => setStatus(q, 'rejected')} data-testid={`sq-reject-${q.id}`}><XCircle className="w-3.5 h-3.5 mr-1 text-rose-600" />Reject</Button>
                      </>)}
                      {isAdmin && q.status === 'approved' && (
                        <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700" onClick={() => openConvert(q)} data-testid={`sq-convert-${q.id}`}>
                          <ArrowRightCircle className="w-3.5 h-3.5 mr-1" />Convert
                        </Button>
                      )}
                      {isAdmin && q.status !== 'converted' && <Button size="sm" variant="ghost" onClick={() => del(q)} data-testid={`sq-delete-${q.id}`}><Trash2 className="w-3.5 h-3.5 text-rose-600" /></Button>}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create/Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? `Edit ${editing.quotation_number}` : 'New Sales Quotation'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <Label>Customer *</Label>
                <Select value={form.customer_id} onValueChange={(v) => setForm({ ...form, customer_id: v })}>
                  <SelectTrigger data-testid="sq-customer"><SelectValue placeholder="Select customer" /></SelectTrigger>
                  <SelectContent>{customers.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Quotation Date *</Label>
                <Input type="date" value={form.quotation_date} onChange={(e) => setForm({ ...form, quotation_date: e.target.value })} required data-testid="sq-date" />
              </div>
              <div>
                <Label>Valid Until</Label>
                <Input type="date" value={form.valid_until} onChange={(e) => setForm({ ...form, valid_until: e.target.value })} data-testid="sq-valid-until" />
              </div>
              <div className="md:col-span-2">
                <Label>Customer Reference / Indent No.</Label>
                <Input value={form.customer_reference} onChange={(e) => setForm({ ...form, customer_reference: e.target.value })} placeholder="Buyer's PO / indent reference" data-testid="sq-reference" />
              </div>
              <div className="md:col-span-3">
                <Label>Notes</Label>
                <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-semibold">Items</Label>
                <Button type="button" size="sm" variant="outline" onClick={() => setForm({ ...form, items: [...form.items, { ...emptyItem }] })} data-testid="sq-add-item"><Plus className="w-3.5 h-3.5 mr-1" />Add Line</Button>
              </div>
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Product</TableHead>
                      <TableHead className="w-[130px]">Buyer Ref</TableHead>
                      <TableHead className="w-[80px]">Qty</TableHead>
                      <TableHead className="w-[100px]">Rate</TableHead>
                      <TableHead className="w-[80px]">Disc %</TableHead>
                      <TableHead className="w-[80px]">GST %</TableHead>
                      <TableHead className="w-[40px]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {form.items.map((it, idx) => (
                      <TableRow key={idx}>
                        <TableCell>
                          <Select value={it.product_id} onValueChange={(v) => onProductChange(idx, v)}>
                            <SelectTrigger data-testid={`sq-item-product-${idx}`} className="h-8"><SelectValue placeholder="Select" /></SelectTrigger>
                            <SelectContent>{products.map(p => <SelectItem key={p.id} value={p.id}>{p.name} {p.size && `(${p.size})`}</SelectItem>)}</SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell>
                          <Input value={it.buyer_item_code || ''} onChange={(e) => setItem(idx, { buyer_item_code: e.target.value })} disabled={!it.product_id} className="h-8" placeholder="—" data-testid={`sq-item-buyer-ref-${idx}`} />
                        </TableCell>
                        <TableCell><Input type="number" min="1" value={it.quantity} onChange={(e) => setItem(idx, { quantity: e.target.value })} className="h-8" data-testid={`sq-item-qty-${idx}`} /></TableCell>
                        <TableCell><Input type="number" step="0.01" value={it.selling_price} onChange={(e) => setItem(idx, { selling_price: e.target.value })} className="h-8" data-testid={`sq-item-rate-${idx}`} /></TableCell>
                        <TableCell><Input type="number" step="0.01" value={it.discount_percentage} onChange={(e) => setItem(idx, { discount_percentage: e.target.value })} className="h-8" /></TableCell>
                        <TableCell><Input type="number" step="0.01" value={it.gst_percentage} onChange={(e) => setItem(idx, { gst_percentage: e.target.value })} className="h-8" /></TableCell>
                        <TableCell>{form.items.length > 1 && <Button type="button" size="icon" variant="ghost" onClick={() => setForm({ ...form, items: form.items.filter((_, i) => i !== idx) })}><Trash2 className="w-3.5 h-3.5 text-rose-600" /></Button>}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-teal-600 hover:bg-teal-700" disabled={saving} data-testid="sq-save">{saving ? 'Saving…' : editing ? 'Update' : 'Create'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* View dialog */}
      <Dialog open={!!viewing && !convertOpen} onOpenChange={(o) => { if (!o) setViewing(null); }}>
        <DialogContent className="sm:max-w-3xl">
          {viewing && (<>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">{viewing.quotation_number} <Badge className={STATUS_COLORS[viewing.status]}>{viewing.status}</Badge></DialogTitle>
              <DialogDescription>{viewing.customer_name} · {viewing.quotation_date}{viewing.customer_reference ? ` · Ref: ${viewing.customer_reference}` : ''}</DialogDescription>
            </DialogHeader>
            <div className="rounded-md border">
              <Table>
                <TableHeader><TableRow><TableHead>Product</TableHead><TableHead>HSN</TableHead><TableHead>Buyer Ref</TableHead><TableHead className="text-right">Qty</TableHead><TableHead className="text-right">Rate</TableHead><TableHead className="text-right">Total</TableHead></TableRow></TableHeader>
                <TableBody>
                  {viewing.items.map((it, i) => (
                    <TableRow key={i}>
                      <TableCell className="text-xs">{it.product_name} <span className="text-slate-400">{it.brand}</span></TableCell>
                      <TableCell className="text-xs">{it.hsn_code}</TableCell>
                      <TableCell className="text-xs tabular-nums">{it.buyer_item_code || '—'}</TableCell>
                      <TableCell className="text-right tabular-nums">{it.quantity}</TableCell>
                      <TableCell className="text-right tabular-nums">₹{Number(it.selling_price).toFixed(2)}</TableCell>
                      <TableCell className="text-right tabular-nums">₹{Number(it.total_amount).toFixed(2)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <div className="text-right space-y-1 text-sm">
              <div>Taxable: <span className="tabular-nums">₹{Number(viewing.total_taxable).toFixed(2)}</span></div>
              {viewing.is_interstate
                ? <div>IGST: <span className="tabular-nums">₹{Number(viewing.total_igst).toFixed(2)}</span></div>
                : <><div>CGST: <span className="tabular-nums">₹{Number(viewing.total_cgst).toFixed(2)}</span></div>
                    <div>SGST: <span className="tabular-nums">₹{Number(viewing.total_sgst).toFixed(2)}</span></div></>}
              <div className="font-semibold">Grand Total: <span className="tabular-nums">₹{Number(viewing.grand_total).toFixed(2)}</span></div>
              {viewing.converted_invoice_number && <div className="text-xs text-indigo-700">Converted → Invoice {viewing.converted_invoice_number}</div>}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => downloadPdf(viewing)} data-testid="sq-view-download-pdf">
                <Download className="w-4 h-4 mr-1" /> Download PDF
              </Button>
              {isAdmin && viewing.status !== 'converted' && <Button variant="outline" onClick={() => { setDialogOpen(true); openEdit(viewing); setViewing(null); }}>Edit</Button>}
              <Button variant="outline" onClick={() => setViewing(null)}>Close</Button>
            </DialogFooter>
          </>)}
        </DialogContent>
      </Dialog>

      {/* Convert dialog */}
      <Dialog open={convertOpen} onOpenChange={setConvertOpen}>
        <DialogContent className="sm:max-w-2xl">
          {viewing && (<>
            <DialogHeader>
              <DialogTitle>Convert {viewing.quotation_number} → Invoice</DialogTitle>
              <DialogDescription>Pick the batch to issue for each line. Product / quantity / rate / GST / Buyer Item Code are carried from the quotation.</DialogDescription>
            </DialogHeader>
            <Table>
              <TableHeader><TableRow><TableHead>Product</TableHead><TableHead className="w-[120px]">Buyer Ref</TableHead><TableHead className="w-[70px]">Qty</TableHead><TableHead>Batch</TableHead></TableRow></TableHeader>
              <TableBody>
                {viewing.items.map((it, idx) => (
                  <TableRow key={idx}>
                    <TableCell className="text-xs">{it.product_name}</TableCell>
                    <TableCell className="text-xs tabular-nums">{it.buyer_item_code || '—'}</TableCell>
                    <TableCell className="text-right tabular-nums">{it.quantity}</TableCell>
                    <TableCell>
                      <Select value={convertBatches[idx]?.batch_number || ''} onValueChange={(v) => setConvertBatches(prev => { const c = [...prev]; c[idx] = { ...c[idx], batch_number: v }; return c; })}>
                        <SelectTrigger className="h-8" data-testid={`sq-convert-batch-${idx}`}><SelectValue placeholder="Select batch" /></SelectTrigger>
                        <SelectContent>{batchesForProduct(it.product_id).map(b => <SelectItem key={b.batch_number} value={b.batch_number}>{b.batch_number} ({b.quantity})</SelectItem>)}</SelectContent>
                      </Select>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <DialogFooter>
              <Button variant="outline" onClick={() => setConvertOpen(false)}>Cancel</Button>
              <Button className="bg-indigo-600 hover:bg-indigo-700" onClick={runConvert} data-testid="sq-convert-confirm">Convert to Invoice</Button>
            </DialogFooter>
          </>)}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SalesQuotations;
